import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Stack,
  Typography,
  alpha,
} from '@mui/material';
import {
  ArrowForward,
  ElectricMeter,
  ErrorOutline,
  EventAvailable,
} from '@mui/icons-material';
import type { Theme } from '@mui/material/styles';
import type { ConsumptionReminderAlert } from '@/helpers/consumptionReminderAlerts';

const alertStyleMap = {
  DUE: {
    icon: EventAvailable,
    iconColor: 'warning.main',
    borderColor: (theme: Theme) => alpha(theme.palette.warning.main, 0.36),
    background: (theme: Theme) => alpha(theme.palette.warning.main, 0.08),
    titleColor: 'warning.dark',
  },
  OVERDUE: {
    icon: ErrorOutline,
    iconColor: 'error.main',
    borderColor: (theme: Theme) => alpha(theme.palette.error.main, 0.35),
    background: (theme: Theme) => alpha(theme.palette.error.main, 0.05),
    titleColor: 'error.dark',
  },
} as const;

interface ConsumptionReminderAlertCardProps {
  alert: ConsumptionReminderAlert;
  showBuildingName?: boolean;
}

const ConsumptionReminderAlertCard = ({
  alert,
  showBuildingName = false,
}: ConsumptionReminderAlertCardProps) => {
  const styles = alertStyleMap[alert.alertType];
  const SeverityIcon = styles.icon;

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: styles.borderColor,
        background: styles.background,
        borderRadius: 3,
        p: 1.5,
        '@media (min-width: 1800px)': { p: 2 },
        height: '100%',
        minWidth: 0,
      }}
    >
      <Stack direction="row" spacing={{ xs: 1.25, sm: 1 }} alignItems="flex-start" sx={{ height: '100%' }}>
        <SeverityIcon
          sx={{
            color: styles.iconColor,
            mt: 0.25,
            fontSize: { xs: 22, sm: 20 },
            '@media (min-width: 1800px)': { fontSize: 24 },
          }}
        />
        <Box sx={{ display: 'flex', flex: 1, flexDirection: 'column', minWidth: 0, height: '100%' }}>
          <Typography
            variant="subtitle2"
            sx={{
              color: styles.titleColor,
              fontWeight: 700,
              fontSize: { xs: '0.875rem', sm: '0.75rem' },
              overflowWrap: 'break-word',
              '@media (min-width: 1800px)': { fontSize: '0.875rem' },
            }}
          >
            {alert.title}
          </Typography>

          {showBuildingName && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
              Objekt: {alert.buildingName}
            </Typography>
          )}

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              fontSize: { sm: '0.75rem' },
              overflowWrap: 'break-word',
              '@media (min-width: 1800px)': { fontSize: '0.875rem' },
            }}
          >
            {alert.message}
          </Typography>

          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.25 }}>
            <ElectricMeter
              sx={{
                color: 'text.secondary',
                fontSize: { xs: 18, sm: 16 },
                '@media (min-width: 1800px)': { fontSize: 18 },
              }}
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: { sm: '0.6875rem' },
                overflowWrap: 'break-word',
                '@media (min-width: 1800px)': { fontSize: '0.75rem' },
              }}
            >
              Odběrné místo: {alert.collectionPointName}
            </Typography>
          </Stack>

          <Button
            component={RouterLink}
            to={alert.actionPath}
            size="small"
            endIcon={<ArrowForward fontSize="small" />}
            sx={{
              px: 0,
              mt: 'auto',
              minWidth: 0,
              alignSelf: 'flex-start',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: { sm: '0.75rem' },
              textAlign: 'left',
              '@media (min-width: 1800px)': { fontSize: '0.8125rem' },
            }}
          >
            Nahrát odečet
          </Button>
        </Box>
      </Stack>
    </Box>
  );
};

export default ConsumptionReminderAlertCard;
